import { Link, Outlet, useLocation } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { useTheme } from '@/app/theme';
import type { ThemePreference } from '@/modules/shared/storage';
import { AdSlot } from '@/modules/shared/ads/AdSlot';
import { cn } from '@/modules/shared/cn';

const THEME_CYCLE: ThemePreference[] = ['system', 'light', 'dark'];

export function AppShell() {
  const { t, locale, setLocale } = useI18n();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const isHome = location.pathname === '/';

  const themeLabel =
    theme === 'light' ? t('themeLight') : theme === 'dark' ? t('themeDark') : t('themeSystem');

  const cycleTheme = () => {
    const idx = THEME_CYCLE.indexOf(theme);
    setTheme(THEME_CYCLE[(idx + 1) % THEME_CYCLE.length] ?? 'system');
  };

  return (
    <div className="flex min-h-dvh flex-col pb-[env(safe-area-inset-bottom)]">
      <header className="sticky top-0 z-20 border-b border-dp-line/50 bg-dp-surface/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-lg items-center justify-between gap-2 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            {!isHome && (
              <Link
                to="/"
                className="shrink-0 rounded-lg px-2 py-1 text-sm font-semibold text-dp-muted hover:bg-dp-line/40"
              >
                ← {t('backHome')}
              </Link>
            )}
            <Link to="/" className="truncate font-display text-lg font-extrabold tracking-tight">
              {t('brand')}
            </Link>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => setLocale(locale === 'pt' ? 'en' : 'pt')}
              className="rounded-lg px-2.5 py-1.5 text-xs font-bold uppercase tracking-wide text-dp-muted hover:bg-dp-line/40"
              aria-label={t('language')}
            >
              {locale === 'pt' ? 'PT' : 'EN'}
            </button>
            <button
              type="button"
              onClick={cycleTheme}
              className="rounded-lg px-2.5 py-1.5 text-xs font-bold text-dp-muted hover:bg-dp-line/40"
              aria-label={themeLabel}
            >
              {theme === 'dark' ? '☾' : theme === 'light' ? '☀' : '◐'}
            </button>
          </div>
        </div>
      </header>

      <main className={cn('mx-auto w-full max-w-lg flex-1 px-4 py-5', 'fade-up')}>
        <Outlet />
      </main>

      <footer className="border-t border-dp-line/40 px-4 py-4 text-center text-xs text-dp-muted">
        <AdSlot slot={isHome ? 'landing-footer' : 'game-footer'} />
        <div className="mt-2 flex items-center justify-center gap-3">
          <Link to="/privacy" className="hover:text-dp-ink">
            {t('privacy')}
          </Link>
          <span aria-hidden>·</span>
          <Link to="/terms" className="hover:text-dp-ink">
            {t('terms')}
          </Link>
        </div>
        <p className="mt-2 opacity-80">{t('footerNote')}</p>
      </footer>
    </div>
  );
}
